import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildCloudinaryUrl } from "@/lib/cloudinaryUrl";
import { CarouselSkeleton } from "./CarouselSkeleton";
import type { PortfolioImage, Artist } from "@/types";

// The preview slot is at most ~500px tall; 1200w + dpr_auto keeps retina
// screens sharp while letting the CDN downscale the stored master.
const PREVIEW_WIDTH = 1200;

function previewSrc(image: PortfolioImage): string {
  return buildCloudinaryUrl(image.url, { width: PREVIEW_WIDTH }) || image.url;
}

interface ThumbnailCarouselProps {
  images: PortfolioImage[];
  artist: Artist;
  onImageClick: (index: number) => void;
  visibleCount?: { mobile: number; tablet: number; desktop: number };
  isLoading?: boolean;
}

export function ThumbnailCarousel({
  images,
  artist,
  onImageClick,
  visibleCount = { mobile: 3, tablet: 5, desktop: 7 },
  isLoading = false,
}: ThumbnailCarouselProps) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [visible, setVisible] = useState(visibleCount.desktop);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Image ids whose high-res preview finished loading — until then the
  // blurred thumbnail backdrop stands in (blur-up placeholder).
  const [loadedIds, setLoadedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const updateVisible = () => {
      if (window.innerWidth < 640) {
        setVisible(visibleCount.mobile);
      } else if (window.innerWidth < 1024) {
        setVisible(visibleCount.tablet);
      } else {
        setVisible(visibleCount.desktop);
      }
    };

    updateVisible();
    window.addEventListener("resize", updateVisible);
    return () => window.removeEventListener("resize", updateVisible);
  }, [visibleCount]);

  useEffect(() => {
    setScrollPosition(0);
    setSelectedIndex(0);
  }, [images]);

  const maxScroll = Math.max(0, images.length - visible);
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < maxScroll;

  const scrollLeft = () => setScrollPosition((p) => Math.max(0, p - 1));
  const scrollRight = () => setScrollPosition((p) => Math.min(maxScroll, p + 1));

  // Selecting via keyboard keeps the strip scrolled to the selected thumb.
  const selectIndex = (index: number) => {
    const clamped = Math.max(0, Math.min(images.length - 1, index));
    setSelectedIndex(clamped);
    setScrollPosition((p) => {
      if (clamped < p) return clamped;
      if (clamped >= p + visible) return Math.min(maxScroll, clamped - visible + 1);
      return p;
    });
  };

  // Preload the neighbors of the selected image so switching is instant.
  useEffect(() => {
    [selectedIndex - 1, selectedIndex + 1].forEach((i) => {
      if (images[i]) {
        const img = new Image();
        img.src = previewSrc(images[i]);
      }
    });
  }, [selectedIndex, images]);

  if (isLoading) {
    return <CarouselSkeleton count={visible} />;
  }

  if (images.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border)]">
        <p className="text-[var(--color-text-muted)]">No artwork yet</p>
      </div>
    );
  }

  const itemWidth = 100 / visible;
  const gapPercent = 0.75;
  const translateX = -scrollPosition * (itemWidth + gapPercent);
  const selectedImage = images[selectedIndex];

  return (
    <div className="space-y-4">
      {/* Preview Section */}
      <div
        className="group/preview relative w-full h-[300px] sm:h-[400px] lg:h-[500px] rounded-xl overflow-hidden bg-[var(--color-bg-card)] border border-[var(--color-border)] cursor-pointer flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
        onClick={() => onImageClick(selectedIndex)}
        tabIndex={0}
        role="button"
        aria-label="Open image in full view. Use arrow keys to browse artworks."
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            selectIndex(selectedIndex - 1);
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            selectIndex(selectedIndex + 1);
          } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onImageClick(selectedIndex);
          }
        }}
      >
        {/* Ambient backdrop: the blurred thumbnail fills the letterbox and
            doubles as a blur-up placeholder while the high-res loads. */}
        {(selectedImage.thumbnail || selectedImage.url) && (
          <img
            key={`bg-${selectedImage.id}`}
            src={selectedImage.thumbnail || selectedImage.url}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40 pointer-events-none"
          />
        )}

        <AnimatePresence mode="wait">
          <motion.img
            key={selectedImage.id}
            src={previewSrc(selectedImage)}
            alt={selectedImage.altText || `Art by ${artist.name}`}
            className="relative max-h-full max-w-full w-auto object-contain"
            decoding="async"
            // ref covers cache hits where onLoad can fire before React attaches
            ref={(el: HTMLImageElement | null) => {
              if (el?.complete && el.naturalWidth > 0) {
                setLoadedIds((prev) => {
                  if (prev.has(selectedImage.id)) return prev;
                  const next = new Set(prev);
                  next.add(selectedImage.id);
                  return next;
                });
              }
            }}
            onLoad={() =>
              setLoadedIds((prev) => {
                if (prev.has(selectedImage.id)) return prev;
                const next = new Set(prev);
                next.add(selectedImage.id);
                return next;
              })
            }
            initial={{ opacity: 0 }}
            animate={{ opacity: loadedIds.has(selectedImage.id) ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
        </AnimatePresence>

        {/* Zoom hint */}
        <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[var(--color-bg)]/70 backdrop-blur-sm border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text)] opacity-0 group-hover/preview:opacity-100 transition-opacity pointer-events-none">
          <ZoomIn className="h-4 w-4" />
        </div>

        {/* Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-[var(--color-bg)]/70 backdrop-blur-sm border border-[var(--color-border)] text-xs text-[var(--color-text-muted)] pointer-events-none">
            {selectedIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Section */}
      <div className="relative group">
        <AnimatePresence>
          {canScrollLeft && (
            <motion.button
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              onClick={scrollLeft}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[var(--color-bg)]/80 backdrop-blur-sm border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text)] hover:bg-[var(--color-primary)] hover:text-[var(--color-bg)] transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5" />
            </motion.button>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {canScrollRight && (
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              onClick={scrollRight}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[var(--color-bg)]/80 backdrop-blur-sm border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text)] hover:bg-[var(--color-primary)] hover:text-[var(--color-bg)] transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5" />
            </motion.button>
          )}
        </AnimatePresence>

        <div className="overflow-hidden rounded-xl">
          <motion.div
            className="flex gap-3"
            animate={{ x: `${translateX}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.1}
            onDragEnd={(_, info) => {
              if (info.offset.x < -50 && canScrollRight) scrollRight();
              if (info.offset.x > 50 && canScrollLeft) scrollLeft();
            }}
          >
            {images.map((image, index) => (
              <motion.div
                key={image.id}
                className="flex-shrink-0 cursor-pointer"
                style={{ width: `calc(${itemWidth}% - 0.5rem)` }}
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.2 }}
                onClick={() => setSelectedIndex(index)}
              >
                <div
                  className={cn(
                    "relative aspect-square rounded-lg overflow-hidden bg-[var(--color-bg-card)] border transition-all",
                    selectedIndex === index
                      ? "border-[var(--color-primary)] ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-bg-card)]"
                      : "border-[var(--color-border)] hover:border-[var(--color-primary)]"
                  )}
                >
                  <img
                    src={image.thumbnail || image.url}
                    alt={image.altText || `Art by ${artist.name}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
